import { FC } from 'react';
import { useTranslation } from 'next-i18next';
import Typography from 'src/common/components/Typography';
import PitchPageLayout from 'src/common/components/PitchPageLayout';
import { NEXT_STATIC_IMAGES, SHARED_MEDIA_QUERIES as sharedMediaQueries } from 'src/common/constants';
import { buildSrcSet } from 'src/common/utils/imageHelpers';
import styles from './MobileAppsDetails.module.scss';

const imageWidths = {
  a: 448,
  b: 380,
  c: 335,
};

/**
 * File names match section indexes.
 * Each section has 3 variants, used at specific breakpoints.
*/
const buildUrl = (sectionIndex: number, variant: 'a' | 'b' | 'c') => {
  const src = `${NEXT_STATIC_IMAGES}mobile-apps/sections/${sectionIndex}-${variant}.jpg`;
  return buildSrcSet(src, { w: imageWidths[variant] });
};

const sections = [
  { id: 0, items: [0, 1, 2, 3] }, { id: 1, items: [0, 1, 2] }, { id: 2, items: [0, 1, 2] },
];

const MobileAppsDetails: FC = () => {
  const [t] = useTranslation('mobile-apps-page');

  return (
    <PitchPageLayout>
      <div className={styles.root}>
        <div className={styles.header}>
          <Typography
            size="display-5"
            sizeFromBreakpointM="display-4"
            type="h3"
          >
            {t('details-title')}
          </Typography>
          <Typography
            size="body-xl"
            type="p"
          >
            {t('details-subtitle')}
          </Typography>
        </div>
        {sections.map((section) => (
          <section
            key={section.id}
            className={styles.section}
          >
            <div className={styles.text}>
              <div className={styles.heading}>
                <Typography
                  size="body-l"
                  weight="semibold"
                  type="p"
                >
                  {t(`details-section-${section.id}-title`)}
                </Typography>
                <Typography
                  size="display-5"
                  type="h3"
                >
                  {t(`details-section-${section.id}-subtitle`)}
                </Typography>
              </div>
              <ul>
                {section.items.map((itemId) => (
                  <li key={`${section.id}-item-${itemId}`}>
                    {t(`details-section-${section.id}-item-${itemId}`)}
                  </li>
                ))}
              </ul>
            </div>
            <picture>
              <source
                media={sharedMediaQueries.xxl}
                srcSet={buildUrl(section.id, 'a')}
              />
              <source
                media={sharedMediaQueries.xl}
                srcSet={buildUrl(section.id, 'a')}
              />
              <source
                media={sharedMediaQueries.l}
                srcSet={buildUrl(section.id, 'b')}
              />
              <source
                media={sharedMediaQueries.m}
                srcSet={buildUrl(section.id, 'a')}
              />
              <img
                srcSet={buildUrl(section.id, 'c')}
                alt={t(`details-section-${section.id}-imgAlt`)}
                loading="lazy"
              />
            </picture>
          </section>
        ))}
      </div>
    </PitchPageLayout>
  );
};

export default MobileAppsDetails;
